import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Overline } from "./Reveal";
import { EnquireButton } from "./EnquireButton";
import { Star } from "lucide-react";
import exteriorImg from "@/assets/exterior.png";

const EASE = [0.16, 1, 0.3, 1];

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.25 + i * 0.13, ease: EASE },
  }),
};

const HERO_LINES = ["The Old Pub", "Cellar, Gym", "& Sauna"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden pt-28 pb-14"
    >
      {/* background hero image */}
      <motion.div style={{ y: imgY, scale: 1.08 }} className="absolute inset-0 z-0" data-testid="hero-image">
        <img src={exteriorImg} alt="The Old Pub — the historic Wiltshire Yeoman at golden hour" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/75 to-bg/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-bg/70 via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-4 mb-8"
        >
          <Overline>Chirton · Wiltshire · England</Overline>
          <span className="h-px flex-1 bg-line max-w-[120px]" />
        </motion.div>

        <h1 className="font-serif font-light tracking-tighter leading-[0.92] text-textMain text-5xl sm:text-7xl lg:text-8xl">
          {HERO_LINES.map((l, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {l}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <p className="font-sans text-base md:text-lg text-textMuted max-w-md leading-relaxed">
            A luxurious private cellar suite hidden beneath a converted historic pub — with its own gym, sauna and exclusive guest lounge. A stay that's anything but ordinary.
          </p>
          <div className="flex items-center gap-6">
            <div className="flex flex-col">
              <div className="flex items-center gap-1.5">
                <Star className="w-4 h-4 fill-primary text-primary" />
                <span className="font-serif text-3xl text-textMain">9.8</span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-textMuted mt-1">
                Guest Rating
              </span>
            </div>
            <EnquireButton testId="enquire-hero-btn" />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};
